/**
 * BrandedEmbeddablePlaylist — entry view.
 *
 * V1 scaffold for the 2026 HLTV iframe overhaul. Fully siloed from the legacy
 * partner playlists (HLTVEmbeddablePlaylist, TRN, OPGG, etc.) — owns its own
 * Recoil atoms, its own playlist fetch, its own player stub.
 *
 * Composition:
 *   <BrandedShell>
 *     <TopBannerSlot />              — sponsor mid-clip banner
 *     <PlayerRow><BrandedPlayer/></> — video + sponsor overlays
 *     <PlaylistColumn>               — pill cluster + scrollable rows
 *       <NowPlayingHeader />
 *       <PlaylistScrollRegion>
 *         <PlaylistRow×N />
 *       </>
 *     </>
 *   </>
 */
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { categoryMatches } from "./categories";
import { liftForDarkCanvas } from "./themeContrast";

import { BrandedPlayer } from "./Components/BrandedPlayer";
import { NowPlayingHeader } from "./Components/NowPlayingHeader";
import { PlaylistBannerCard } from "./Components/PlaylistBannerCard";
import { PlaylistRow } from "./Components/PlaylistRow";
import { PlaylistSkeleton } from "./Components/PlaylistSkeleton";
import {
  BrandedActiveClipIndexState,
  BrandedActiveClipState,
  BrandedLayoutVariantState,
  BrandedPlaylistClipsState,
  BrandedPlaylistFilterState,
  BrandedPlaylistLoadingState,
  BrandedSponsorConfigState,
} from "./State";
import {
  BrandedShell,
  PlayerRow,
  PlaylistBannerHeaderSlot,
  PlaylistBannerRailSlot,
  PlaylistBottomFade,
  PlaylistColumn,
  PlaylistHeaderInset,
  PlaylistScrollRegion,
  ScrollMoreButton,
} from "./styledComponents";
import { useAdSlotPhaseMachine, useBrandedPlaylist } from "./ViewModel";

interface BrandedEmbeddablePlaylistProps {
  platform: string;
  playlist: string;
  // Kept for API parity with sibling partner playlists; unused in V1.
  handleMouseEnter?: () => void;
  theme?: string;
}

export const BrandedEmbeddablePlaylist = ({
  platform,
  playlist,
  handleMouseEnter,
}: BrandedEmbeddablePlaylistProps) => {
  useBrandedPlaylist({ playlist, platform });
  useAdSlotPhaseMachine();

  const clips = useRecoilValue(BrandedPlaylistClipsState);
  const activeIndex = useRecoilValue(BrandedActiveClipIndexState);
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const filter = useRecoilValue(BrandedPlaylistFilterState);
  const loading = useRecoilValue(BrandedPlaylistLoadingState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";

  /**
   * Apply the playlist filter for what shows in the tray. The visible
   * list is strictly the category match — we no longer force-include
   * the active clip when it doesn't match the filter, because that
   * read as "the filter is inverted" (you'd click Aces and see your
   * current non-ace clip sitting at the top of the list). A separate
   * effect below handles the case where the active clip drops out of
   * the filtered list by advancing the active pointer to the first
   * remaining match.
   *
   * Filter is a category (`aces` | `clutches` | …) matched via
   * `categoryMatches`; the predicate set is centralized so the header
   * pills and this resolver stay in lock-step.
   */
  const visibleClips = useMemo(() => {
    if (!clips) return [];
    if (!filter) return clips.map((clip, idx) => ({ clip, originalIdx: idx }));
    return clips
      .map((clip, idx) => ({ clip, originalIdx: idx }))
      .filter(({ clip }) => categoryMatches(filter.category, clip));
  }, [clips, filter]);

  /**
   * If the active clip falls out of the filtered set (user clicked a
   * category that doesn't include the currently-playing clip), advance
   * playback to the first clip in the filtered list. Keeps the visible
   * tray strictly = matching set, with the player following along
   * instead of stranding an out-of-filter clip at the top.
   */
  const setActiveClip = useSetRecoilState(BrandedActiveClipState);
  const setActiveIndex = useSetRecoilState(BrandedActiveClipIndexState);
  useEffect(() => {
    if (!filter || !clips?.length || !visibleClips.length) return;
    const activeStillVisible = visibleClips.some(
      (v) => v.originalIdx === activeIndex,
    );
    if (activeStillVisible) return;
    const next = visibleClips[0];
    setActiveIndex(next.originalIdx);
    setActiveClip(next.clip);
  }, [filter, clips, visibleClips, activeIndex, setActiveIndex, setActiveClip]);

  // Up-next = first visible clip whose originalIdx > activeIndex. Falls
  // back to nothing when the active clip is the last match in the filter.
  const upNextOriginalIdx = useMemo(() => {
    const next = visibleClips.find(
      ({ originalIdx }) => originalIdx > activeIndex,
    );
    return next?.originalIdx;
  }, [visibleClips, activeIndex]);

  /**
   * "Scroll for more" chevron at the bottom of the rail. Visibility tracks
   * whether the scroll region has more content past the current viewport
   * — hidden when the rail fits on screen and when the user has scrolled
   * to the end. Replaces the prior 8px branded scrollbar gutter.
   *
   * Tracking is via direct DOM measurement on the ref'd PlaylistScrollRegion
   * (resize-observer + scroll listener); cheaper than a state-per-scroll
   * recoil atom and scoped to just this column.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollChevronVisible, setScrollChevronVisible] = useState(false);

  const measureScrollable = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 4px slack so floating-point rounding doesn't keep the chevron up
    // when we're effectively at the end of the scroll. Both axes are
    // measured so the same chevron works for the desktop vertical rail
    // AND the mobile horizontal rail — only one axis is ever scrollable
    // at a time (CSS toggles overflow-y/overflow-x based on layout
    // mode), so OR'ing them gives the right answer in both cases
    // without the component needing to know which mode it's in.
    const moreVertical = el.scrollHeight - el.clientHeight - el.scrollTop > 4;
    const moreHorizontal = el.scrollWidth - el.clientWidth - el.scrollLeft > 4;
    setScrollChevronVisible(moreVertical || moreHorizontal);
  }, []);

  useEffect(() => {
    measureScrollable();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", measureScrollable, { passive: true });
    const ro = new ResizeObserver(measureScrollable);
    ro.observe(el);
    // Windows mouse wheels only emit deltaY; the mobile rail's
    // overflow-x container ignores deltaY by default, so users on
    // Windows desktop previewing the mobile variant can't scroll the
    // horizontal strip with their wheel. Translate deltaY → scrollLeft
    // whenever the rail's scrollable axis is horizontal. macOS trackpads
    // emit native deltaX which we leave alone (the user's intent is
    // already horizontal — translating deltaY would double-scroll).
    const onWheel = (e: WheelEvent) => {
      const horizontal = el.scrollWidth > el.clientWidth;
      const vertical = el.scrollHeight > el.clientHeight;
      if (!horizontal || vertical) return;
      if (e.deltaY === 0 || e.deltaX !== 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("scroll", measureScrollable);
      el.removeEventListener("wheel", onWheel);
      ro.disconnect();
    };
  }, [measureScrollable, visibleClips.length]);

  const handleScrollDown = () => {
    const el = scrollRef.current;
    if (!el) return;
    // Nudge along whichever axis is actually scrollable. Mobile rail is
    // overflow-x (horizontal cards); desktop is overflow-y (stacked
    // rows). Picking based on which dimension has scrollable content
    // means the same handler powers the vertical "down" chevron AND
    // the rotated "right" chevron in mobile mode.
    const horizontal = el.scrollWidth > el.clientWidth;
    el.scrollBy({
      top: horizontal ? 0 : 80,
      left: horizontal ? 200 : 0,
      behavior: "smooth",
    });
  };

  /**
   * Scoped contrast lift for dark brand colors. A very dark `primary`
   * (e.g. #0d183e navy) collapses into the fixed near-black canvas
   * (--bg #0c1318), making two thin/small surfaces vanish:
   *   - playlist-item borders (driven by --row-bg)
   *   - filter-pill label + count text (driven by --accent)
   * `liftForDarkCanvas` returns a lightened hex ONLY when the input fails
   * the contrast floor; otherwise undefined, so bright/mid brands and the
   * Allstar default are left completely untouched. Border uses the
   * UI-component floor (3:1); pill text uses the small-text floor (4.5:1).
   */
  const rowBorder = liftForDarkCanvas(
    sponsor?.theme?.rowBg ?? sponsor?.theme?.primary,
    3,
  );
  const pillText = liftForDarkCanvas(sponsor?.theme?.primary, 4.5);

  return (
    <BrandedShell
      onMouseEnter={handleMouseEnter}
      $accent={sponsor?.theme?.primary}
      $accentSoft={sponsor?.theme?.primarySoft}
      $accentText={sponsor?.theme?.primaryText}
      $rowBg={sponsor?.theme?.rowBg}
      $rowBorder={rowBorder}
      $pillText={pillText}
      $bottomPlaylist={isBottomPlaylist}
    >
      {loading ? (
        // Keep the BrandedShell wrapper so accent CSS vars + iframe sizing
        // stay consistent on swap; only the inside changes when data lands.
        <PlaylistSkeleton />
      ) : (
        <>
          <PlayerRow $bottomPlaylist={isBottomPlaylist}>
            <BrandedPlayer platform={platform} playlist={playlist} />
          </PlayerRow>
          <PlaylistColumn $bottomPlaylist={isBottomPlaylist}>
            {/* Sponsor lead-in (desktop): the front-of-playlist banner sits
                ABOVE the filter pills per Figma 111:1774. No-ops when the
                campaign has no playlistBanner slot. Hidden on mobile, where
                the in-rail copy below carries it as the first carousel card
                — only one is ever visible, so the impression fires once. */}
            <PlaylistBannerHeaderSlot>
              <PlaylistBannerCard />
            </PlaylistBannerHeaderSlot>
            <PlaylistHeaderInset>
              <NowPlayingHeader />
            </PlaylistHeaderInset>
            <PlaylistScrollRegion
              ref={scrollRef}
              $bottomPlaylist={isBottomPlaylist}
            >
              {/* Sponsor lead-in (mobile): same banner as the first card in
                  the horizontal rail, ahead of the clip rows and unaffected
                  by the category filter so the sold slot always shows.
                  Hidden on desktop (rendered above the filters instead). */}
              <PlaylistBannerRailSlot>
                <PlaylistBannerCard />
              </PlaylistBannerRailSlot>
              {visibleClips.map(({ clip, originalIdx }) => (
                <PlaylistRow
                  key={clip._id ?? `${originalIdx}-${clip.shareId}`}
                  clip={clip}
                  index={originalIdx}
                  isActive={originalIdx === activeIndex}
                  isUpNext={originalIdx === upNextOriginalIdx}
                />
              ))}
            </PlaylistScrollRegion>
            {/* Bottom-edge fade overlay for the vertically-stacked
                bottom-playlist variant. Rendered as a real DOM node
                rather than a ::after pseudo because the pseudo wasn't
                painting over the scroll region's rows in some browsers —
                an actual sibling element with explicit z-index/position
                cannot be missed by the renderer. Hidden in the
                side-rail layout. */}
            {isBottomPlaylist && <PlaylistBottomFade aria-hidden />}
            <ScrollMoreButton
              type="button"
              aria-label="Scroll for more clips"
              onClick={handleScrollDown}
              $visible={scrollChevronVisible}
              $bottomPlaylist={isBottomPlaylist}
            >
              {/* Double chevron pointing down — two stacked `v` glyphs
                  in one SVG so the affordance reads as "more below" at
                  a glance, matching Figma 1:178's chevron pair. */}
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M3 4l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 9l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ScrollMoreButton>
          </PlaylistColumn>
        </>
      )}
    </BrandedShell>
  );
};
